import { z } from 'zod';

// ─── Enums (verbatim from legacy travel_planner_back) ──────────────

// `organizations.task_priority_enum` (DB) ↔ legacy `TaskPriority` enum.
export const taskPriorityValues = ['high', 'medium', 'low'] as const;
export const taskPrioritySchema = z.enum(taskPriorityValues);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

// `organizations.task_profile_enum` (DB) ↔ legacy `TaskProfile`.
export const taskProfileValues = [
  'consultant',
  'finance',
  'operation',
] as const;
export const taskProfileSchema = z.enum(taskProfileValues);
export type TaskProfile = z.infer<typeof taskProfileSchema>;

// `organizations.task_timer_direction_enum` (DB) ↔ legacy.
export const taskTimerDirectionValues = ['before', 'after'] as const;
export const taskTimerDirectionSchema = z.enum(taskTimerDirectionValues);
export type TaskTimerDirection = z.infer<typeof taskTimerDirectionSchema>;

// `organizations.task_timer_reference_enum` (DB) ↔ legacy.
export const taskTimerReferenceValues = [
  'start_of_trip',
  'itinerary_creation',
  'last_change_date',
  'custom_date',
] as const;
export const taskTimerReferenceSchema = z.enum(taskTimerReferenceValues);
export type TaskTimerReference = z.infer<typeof taskTimerReferenceSchema>;

// Legacy `TaskFlag` is stored as integer in `task.flag`.
// NOT_FLAGGED=0, ATTENTION=5, URGENT=10 (verbatim from
// travel_planner_back/src/task/enum/task-flag.enum.ts).
export const taskFlagValues = [0, 5, 10] as const;
export const taskFlagSchema = z.union([
  z.literal(0),
  z.literal(5),
  z.literal(10),
]);
export type TaskFlag = z.infer<typeof taskFlagSchema>;

// ─── Next-actions (consumed by tasksRouter.getNextActions) ─────────

export const nextActionsInput = z.object({
  limit: z.number().int().min(1).max(50).default(20),
});
export type NextActionsInput = z.infer<typeof nextActionsInput>;

export const nextActionItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  priority: taskPrioritySchema,
  itineraryId: z.string().nullable(),
  itineraryTitle: z.string().nullable(),
  expireOn: z.string().nullable(),
  createdOn: z.string(),
});
export type NextActionItem = z.infer<typeof nextActionItemSchema>;

export const nextActionsResponseSchema = z.object({
  items: z.array(nextActionItemSchema),
  count: z.number(),
});
export type NextActionsResponse = z.infer<typeof nextActionsResponseSchema>;

// ─── Task (select / CRUD inputs) ──────────────────────────────────

export const taskSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  createdAccountId: z.uuid(),
  assignedAccountId: z.uuid().nullable(),
  profile: taskProfileSchema,
  priority: taskPrioritySchema,
  duration: z.string().nullable(),
  timerDirection: taskTimerDirectionSchema.nullable(),
  timerReference: taskTimerReferenceSchema,
  description: z.string().nullable(),
  itineraryId: z.uuid().nullable(),
  collectionId: z.uuid().nullable(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
  completedOn: z.string().nullable(),
  flag: taskFlagSchema.nullable(),
  expireOn: z.string().nullable(),
  // Display-only enrichment fields populated by list-style queries via
  // LEFT JOINs. Optional so callers writing into the table (insert/update)
  // can omit them; nullable so list rows with no related FK still pass.
  itineraryRef: z.string().nullable().optional(),
  collectionTitle: z.string().nullable().optional(),
});
export type Task = z.infer<typeof taskSchema>;

// Required fields here mirror the DB NOT NULL columns on
// `taskInOrganizations`: title, profile, priority, timerReference.
// (`createdAccountId` comes from `ctx.user.id` server-side.)
export const createTaskInput = z.object({
  title: z.string().min(1),
  assignedAccountId: z.uuid().optional(),
  profile: taskProfileSchema,
  priority: taskPrioritySchema,
  duration: z.string().optional(),
  timerDirection: taskTimerDirectionSchema.optional(),
  timerReference: taskTimerReferenceSchema,
  description: z.string().max(255).optional(),
  itineraryId: z.uuid().optional(),
  collectionId: z.uuid().optional(),
  flag: taskFlagSchema.optional(),
  expireOn: z.string().optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskInput>;

// Update is a PATCH-style partial. NOTE: `profile`, `priority`, and
// `timerReference` are NOT NULL in the DB but become optional here so
// callers can update one field at a time. The service must therefore
// build the SET clause selectively (omit absent keys) — passing the
// whole partial object to drizzle's `.set()` would NULL out missing
// columns and fail the NOT NULL constraints.
export const updateTaskInput = createTaskInput.partial().extend({
  id: z.uuid(),
  completedOn: z.string().nullable().optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

export const snoozeTaskInput = z.object({
  id: z.uuid(),
  snoozedUntil: z.string().datetime().nullable(),
});
export type SnoozeTaskInput = z.infer<typeof snoozeTaskInput>;

export const taskSortFieldSchema = z.enum([
  'title',
  'priority',
  'profile',
  'itinerary',
  'collection',
  'expireOn',
  'completedOn',
  'createdOn',
]);
export type TaskSortField = z.infer<typeof taskSortFieldSchema>;

export const findTasksInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(50),
  title: z.string().optional(),
  assignedAccountId: z.uuid().optional(),
  // Template mode: when true, return only unassigned tasks
  // (`assignedAccountId IS NULL`) — the collection task library shown on
  // the admin Tasks tab, distinct from a user's assigned tasks. Takes
  // precedence over `assignedAccountId` (templates have no assignee).
  unassignedOnly: z.boolean().optional(),
  collectionIds: z.array(z.uuid()).optional(),
  priorities: z.array(taskPrioritySchema).optional(),
  itineraryIds: z.array(z.uuid()).optional(),
  profiles: z.array(taskProfileSchema).optional(),
  flags: z.array(taskFlagSchema).optional(),
  completed: z.boolean().optional(),
  sortBy: taskSortFieldSchema.optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type FindTasksInput = z.infer<typeof findTasksInput>;

export const paginatedTasksSchema = z.object({
  data: z.array(taskSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});
export type PaginatedTasks = z.infer<typeof paginatedTasksSchema>;

// ─── Action inputs (complete-in-collection, from-set) ─────────────

export const completeTaskInCollectionInput = z.object({
  collectionId: z.uuid(),
  itineraryId: z.uuid(),
});
export type CompleteTaskInCollectionInput = z.infer<
  typeof completeTaskInCollectionInput
>;

export const createTaskFromSetInput = z.object({
  assignedAccountId: z.uuid(),
  collectionId: z.uuid(),
  itineraryId: z.uuid().optional(),
  flag: taskFlagSchema.optional(),
});
export type CreateTaskFromSetInput = z.infer<typeof createTaskFromSetInput>;

export const setTaskFlagInput = z.object({
  id: z.uuid(),
  flag: taskFlagSchema,
});
export type SetTaskFlagInput = z.infer<typeof setTaskFlagInput>;
